import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import {
  ArrowLeft,
  Banknote,
  Boxes,
  CalendarRange,
  CheckCircle2,
  ChevronRight,
  Home,
  Minus,
  Plus,
  Receipt,
  Search,
  ShoppingCart,
  Smartphone,
  Trash2,
  Truck,
  UserRound,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { getAllClientsData, getGeneralClientsSummary, searchGeneralProducts, searchPhones } from "../../api/products/get"
import { sellGeneralProduct, sellPhone, updateGeneralProduct } from "../../api/products/post"
import { getCurrentPlan, hasFeature } from "../../utils/permissions"
import { notifyDashboardRefresh } from "../../utils/dashboardRefresh"
import { getStoreExperience } from "../../utils/storeExperience"
import { isCatalogStore, isPropertyStore } from "../../utils/storeCategory"

type StepId = "products" | "customer" | "payment"

type ExistingCustomer = {
  name: string
  phone: string
  totalDebt: number
  purchases: number
}

type CartLine = {
  key: string
  productId: string
  label: string
  reference: string
  stock: number
  quantity: number
  unitPrice: number
  category: "GENERAL" | "PHONE" | "PROPERTY"
  listingType?: string
  district?: string
  location?: string
  transactionType?: string
  stayDays?: string
  rentalStartDate?: string
  rentalEndDate?: string
  rentalAvailability?: string
}

const STEPS: Array<{ id: StepId; label: string; description: string }> = [
  {
    id: "products",
    label: "Produits",
    description: "Selection et panier client.",
  },
  {
    id: "customer",
    label: "Client",
    description: "Choix d'un client existant ou nouveau.",
  },
  {
    id: "payment",
    label: "Paiement",
    description: "Reglement et validation finale.",
  },
]

export default function SaleStudioPage() {
  const navigate = useNavigate()
  const { userdata } = useSelector((state: any) => state.UserReducer)
  const currentPlan = getCurrentPlan(userdata)
  const hasMobileMoney = hasFeature(currentPlan, "MOBILE_MONEY")
  const store = userdata?.store
  const storeId = store?._id
  const isGeneralStore = isCatalogStore(store)
  const propertyStore = isPropertyStore(store)
  const experience = getStoreExperience(store)
  const supportsCart = isGeneralStore && !propertyStore
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const customerContainerRef = useRef<HTMLDivElement>(null)

  const [activeStep, setActiveStep] = useState(0)
  const [productQuery, setProductQuery] = useState("")
  const [productResults, setProductResults] = useState<any[]>([])
  const [productSearchLoading, setProductSearchLoading] = useState(false)
  const [cart, setCart] = useState<CartLine[]>([])
  const [customers, setCustomers] = useState<ExistingCustomer[]>([])
  const [customerQuery, setCustomerQuery] = useState("")
  const [selectedExistingCustomer, setSelectedExistingCustomer] = useState<ExistingCustomer | null>(null)
  const [clientName, setClientName] = useState("")
  const [clientPhone, setClientPhone] = useState("")
  const [paid, setPaid] = useState("")
  const [paymentMethod, setPaymentMethod] = useState("CASH")
  const [payOnline, setPayOnline] = useState(false)
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const currentStepId = STEPS[activeStep]?.id
  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase()
    if (!q) return customers.slice(0, 8)

    return customers
      .filter((customer) =>
        customer.name.toLowerCase().includes(q) || customer.phone.toLowerCase().includes(q)
      )
      .slice(0, 8)
  }, [customerQuery, customers])

  useEffect(() => {
    if (!storeId) return

    const loadCustomers = async () => {
      try {
        const response = isGeneralStore
          ? await getGeneralClientsSummary(storeId, 1, 100, false)
          : await getAllClientsData(storeId, 1, 100, false)
        const rows = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : []

        const uniqueCustomers = new Map<string, ExistingCustomer>()
        rows.forEach((row: any) => {
          const phone = String(row?.phone || row?.clientPhone || "").trim()
          if (!phone) return

          uniqueCustomers.set(phone, {
            name: String(row?.name || row?.clientName || "Client"),
            phone,
            totalDebt: Number(row?.totalDebt || 0),
            purchases: Number(row?.purchases || 0),
          })
        })

        setCustomers(Array.from(uniqueCustomers.values()))
      } catch (customerError) {
        console.error(customerError)
        setCustomers([])
      }
    }

    loadCustomers()
  }, [isGeneralStore, storeId])

  useEffect(() => {
    if (!storeId || !productQuery || productQuery.trim().length < 2) {
      setProductResults([])
      return
    }

    const fetchItems = async () => {
      try {
        setProductSearchLoading(true)
        const data = isGeneralStore
          ? await searchGeneralProducts(storeId, productQuery)
          : await searchPhones(storeId, productQuery)
        setProductResults(Array.isArray(data) ? data : [])
      } catch (e) {
        console.error(e)
        setProductResults([])
      } finally {
        setProductSearchLoading(false)
      }
    }

    const debounce = setTimeout(fetchItems, 250)
    return () => clearTimeout(debounce)
  }, [isGeneralStore, productQuery, storeId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (searchContainerRef.current && !searchContainerRef.current.contains(target)) {
        setProductResults([])
      }

      if (customerContainerRef.current && !customerContainerRef.current.contains(target)) {
        setCustomerQuery("")
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const totalAmount = useMemo(
    () => cart.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0),
    [cart]
  )
  const paidAmount = Number(paid || 0)
  const remainingAmount = Math.max(0, totalAmount - paidAmount)

  const canAddMoreProducts = supportsCart || cart.length === 0
  const pageTitle = propertyStore
    ? "Vente pas a pas"
    : supportsCart
      ? "Vente multi-produits"
      : "Vente pas a pas"
  const pageSubtitle = supportsCart
    ? "Composez un panier, rattachez-le a un client, puis validez le reglement dans un parcours plus propre."
    : "Un parcours guide pour preparer la vente, choisir le client et confirmer le reglement."

  const addProductToCart = (item: any) => {
    const label = item?.model?.name || item?.name || item?.label || "Produit"
    const reference = item?.imei || item?.identifier || item?.sku || item?.code || ""
    const productId = String(item?._id || "")
    const stock = Number(item?.quantityInStock ?? item?.quantity ?? item?.stock ?? 0)
    const listingType = String(item?.listingType || "SALE").toUpperCase()
    const unitPrice = Number(
      propertyStore
        ? resolveSuggestedPropertyAmount(item, resolveInitialTransactionType(listingType, "SALE"))
        : item?.sellPrice || item?.price || 0
    )

    setCart((current) => {
      if (!supportsCart && current.length > 0) {
        return [
          {
            ...buildCartLine(item, label, reference, stock, unitPrice, propertyStore),
            key: `${productId}-0`,
          },
        ]
      }

      const existingIndex = current.findIndex((line) => line.productId === productId)
      if (existingIndex >= 0) {
        return current.map((line, index) =>
          index === existingIndex
            ? {
                ...line,
                quantity: supportsCart ? Math.min(line.quantity + 1, Math.max(stock, line.quantity + 1)) : 1,
              }
            : line
        )
      }

      return [
        ...current,
        {
          ...buildCartLine(item, label, reference, stock, unitPrice, propertyStore),
          key: `${productId}-${current.length}`,
        },
      ]
    })

    setProductQuery("")
    setProductResults([])
    setError("")
  }

  const updateCartLine = (key: string, patch: Partial<CartLine>) => {
    setCart((current) =>
      current.map((line) => {
        if (line.key !== key) return line

        const nextLine = { ...line, ...patch }
        if (!supportsCart) {
          nextLine.quantity = 1
        }
        return nextLine
      })
    )
  }

  const removeCartLine = (key: string) => {
    setCart((current) => current.filter((line) => line.key !== key))
  }

  const selectExistingCustomer = (customer: ExistingCustomer) => {
    setSelectedExistingCustomer(customer)
    setClientName(customer.name)
    setClientPhone(customer.phone)
    setCustomerQuery("")
    setError("")
  }

  const validateProductsStep = () => {
    if (!cart.length) {
      return "Ajoutez au moins un produit a vendre"
    }

    for (const line of cart) {
      if (!line.productId) return "Un produit du panier est invalide"
      if (!Number.isFinite(line.unitPrice) || line.unitPrice < 0) {
        return `Prix invalide pour ${line.label}`
      }
      if (!Number.isFinite(line.quantity) || line.quantity <= 0) {
        return `Quantite invalide pour ${line.label}`
      }
      if (line.stock > 0 && line.quantity > line.stock) {
        return `Stock insuffisant pour ${line.label}. Disponible: ${line.stock}`
      }
      if (propertyStore && line.transactionType === "RENT") {
        if (!line.rentalStartDate || !line.rentalEndDate) {
          return `Renseignez la periode de location pour ${line.label}`
        }
        if (!line.stayDays || Number(line.stayDays) <= 0) {
          return `Renseignez le nombre de jours pour ${line.label}`
        }
        if (new Date(line.rentalEndDate).getTime() < new Date(line.rentalStartDate).getTime()) {
          return `La date de fin doit etre apres la date de debut pour ${line.label}`
        }
      }
    }

    return ""
  }

  const validateCustomerStep = () => {
    if (!clientName.trim()) return "Renseignez le nom du client"
    if (!clientPhone.trim()) return "Renseignez le telephone du client"
    return ""
  }

  const validatePaymentStep = () => {
    if (!paid && paid !== "0") return "Renseignez le montant paye"
    if (!Number.isFinite(paidAmount) || paidAmount < 0) {
      return "Le montant paye est invalide"
    }
    if (paidAmount > totalAmount) {
      return "Le montant paye depasse le total de la transaction"
    }
    if (paymentMethod === "MOBILE_MONEY" && !hasMobileMoney) {
      return "Mobile Money est disponible a partir du plan PRO."
    }
    return ""
  }

  const stepValidators: Record<StepId, () => string> = {
    products: validateProductsStep,
    customer: validateCustomerStep,
    payment: validatePaymentStep,
  }

  const goNext = () => {
    const validationError = stepValidators[currentStepId]?.()
    if (validationError) {
      setError(validationError)
      return
    }

    setError("")
    setActiveStep((current) => Math.min(current + 1, STEPS.length - 1))
  }

  const goBack = () => {
    if (activeStep === 0) {
      navigate("/dashboard")
      return
    }

    setError("")
    setActiveStep((current) => Math.max(current - 1, 0))
  }

  const handleConfirm = async () => {
    const productError = validateProductsStep()
    const customerError = validateCustomerStep()
    const paymentError = validatePaymentStep()
    const firstError = productError || customerError || paymentError

    if (firstError) {
      setError(firstError)
      return
    }

    try {
      setSubmitting(true)
      setError("")

      let remainingPaidToAllocate = paidAmount

      for (const line of cart) {
        const lineTotal = line.unitPrice * line.quantity
        const linePaid = Math.min(remainingPaidToAllocate, lineTotal)
        remainingPaidToAllocate = Math.max(0, remainingPaidToAllocate - linePaid)

        if (isGeneralStore) {
          await sellGeneralProduct(storeId, line.productId, {
            productId: line.productId,
            quantity: line.quantity,
            unitSellPrice: line.unitPrice,
            sellPrice: lineTotal,
            paid: linePaid,
            clientName: clientName.trim(),
            clientPhone: clientPhone.trim(),
            paymentMethod,
            payOnline,
            transactionType: propertyStore ? line.transactionType : "SALE",
            listingType: propertyStore ? line.listingType : undefined,
            stayDays: propertyStore && line.transactionType === "RENT" ? Number(line.stayDays || 0) : undefined,
            rentalDays: propertyStore && line.transactionType === "RENT" ? Number(line.stayDays || 0) : undefined,
            rentalStartDate: propertyStore ? line.rentalStartDate || undefined : undefined,
            rentalEndDate: propertyStore ? line.rentalEndDate || undefined : undefined,
            availability: propertyStore ? line.rentalAvailability : undefined,
          })

          if (propertyStore && line.rentalAvailability) {
            try {
              await updateGeneralProduct(storeId, line.productId, {
                availability: line.rentalAvailability,
              })
            } catch (availabilityError) {
              console.warn("Unable to sync property availability", availabilityError)
            }
          }
        } else {
          await sellPhone(line.productId, {
            storeId,
            sellPrice: line.unitPrice,
            paid: linePaid,
            clientName: clientName.trim(),
            clientPhone: clientPhone.trim(),
            paymentMethod,
          })
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 1800))
      notifyDashboardRefresh()
      navigate("/sales")
    } catch (submitError: any) {
      setError(submitError?.message || "Erreur lors de la transaction")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <section className={`relative overflow-hidden rounded-3xl border border-white/40 bg-gradient-to-br ${experience.stock.heroClass} p-6 text-white shadow-xl sm:p-7`}>
        <div className={`pointer-events-none absolute -top-10 right-0 h-36 w-36 rounded-full blur-3xl ${experience.stock.heroGlowClass}`} />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 transition hover:bg-white/20"
            >
              <ArrowLeft size={14} />
              Retour tableau de bord
            </button>
            <p className="mt-4 inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
              <ShoppingCart size={12} />
              Checkout stepper
            </p>
            <h1 className="mt-3 text-2xl font-bold sm:text-3xl">{pageTitle}</h1>
            <p className="mt-2 max-w-2xl text-sm text-white/85">{pageSubtitle}</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <HeroStat label="Lignes" value={String(cart.length)} />
            <HeroStat label="Total" value={`${totalAmount.toLocaleString()} FCFA`} />
            <HeroStat label="Reste" value={`${remainingAmount.toLocaleString()} FCFA`} />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-3">
          {STEPS.map((step, index) => {
            const isActive = index === activeStep
            const isCompleted = index < activeStep

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => {
                  if (index <= activeStep) {
                    setError("")
                    setActiveStep(index)
                  }
                }}
                className={`rounded-2xl border p-4 text-left transition ${
                  isActive
                    ? "border-slate-900 bg-slate-900 text-white"
                    : isCompleted
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                } ${index > activeStep ? "cursor-default" : ""}`}
              >
                <p className="text-xs uppercase tracking-[0.16em]">{`Etape 0${index + 1}`}</p>
                <p className="mt-2 text-base font-semibold">{step.label}</p>
                <p className={`mt-1 text-sm ${isActive ? "text-white/75" : ""}`}>{step.description}</p>
              </button>
            )
          })}
        </div>
      </section>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[1.12fr_0.88fr]">
        <section className={`rounded-3xl border p-5 shadow-sm ${experience.stock.panelClass}`}>
          {currentStepId === "products" && (
            <div className="space-y-6">
              <div ref={searchContainerRef} className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={productQuery}
                  onChange={(e) => {
                    setProductQuery(e.target.value)
                    setError("")
                  }}
                  placeholder={
                    propertyStore
                      ? "Rechercher un bien, une reference ou un quartier..."
                      : isGeneralStore
                        ? "Rechercher un produit ou une reference..."
                        : "Rechercher par modele ou IMEI..."
                  }
                  className="w-full rounded-2xl border border-slate-200 pl-9 px-3 py-3"
                />

                {productResults.length > 0 && (
                  <div className="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto divide-y overflow-hidden rounded-2xl border bg-white shadow-xl">
                    {productResults.map((item) => {
                      const label = item?.model?.name || item?.name || item?.label || "Produit"
                      const reference = item?.imei || item?.identifier || item?.sku || item?.code
                      const stock = Number(item?.quantityInStock ?? item?.quantity ?? item?.stock ?? 0)

                      return (
                        <button
                          key={item._id}
                          type="button"
                          onClick={() => addProductToCart(item)}
                          disabled={!canAddMoreProducts}
                          className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${propertyStore ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-600"}`}>
                              {propertyStore ? <Home size={16} /> : <Smartphone size={16} />}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-800">{label}</p>
                              {reference && (
                                <p className="text-xs text-slate-500">
                                  {isGeneralStore ? "Ref." : "IMEI"}: {reference}
                                </p>
                              )}
                              {propertyStore && (
                                <p className="text-xs text-slate-500">{item?.district || item?.location || "Bien immobilier"}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            {supportsCart && (
                              <p className="text-xs font-semibold text-emerald-700">{stock} stock</p>
                            )}
                            <p className="text-xs text-slate-500">
                              {Number(item?.sellPrice || item?.price || item?.dailyRentalPrice || 0).toLocaleString()} FCFA
                            </p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}

                {productSearchLoading && <p className="mt-2 text-xs text-slate-400">Recherche...</p>}
                {!canAddMoreProducts && (
                  <p className="mt-2 text-xs text-amber-600">
                    Ce type de boutique prend une seule reference par transaction.
                  </p>
                )}
              </div>

              <div className="space-y-4">
                {cart.map((line) => (
                  <div key={line.key} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-800">{line.label}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {line.reference ? `${isGeneralStore ? "Ref." : "IMEI"}: ${line.reference}` : "Sans reference"}
                        </p>
                        {line.category === "PROPERTY" && (
                          <p className="mt-1 text-xs text-slate-500">{line.district || line.location || "Bien immobilier"}</p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeCartLine(line.key)}
                        className="rounded-xl border border-red-200 p-2 text-red-600 transition hover:bg-red-50"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      {supportsCart && (
                        <Field label="Quantite">
                          <div className="mt-2 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => updateCartLine(line.key, { quantity: Math.max(1, line.quantity - 1) })}
                              className="rounded-xl border border-slate-200 p-2"
                            >
                              <Minus size={14} />
                            </button>
                            <input
                              type="number"
                              min={1}
                              value={line.quantity}
                              onChange={(e) =>
                                updateCartLine(line.key, {
                                  quantity: Math.max(1, Number(e.target.value) || 1),
                                })
                              }
                              className="w-full rounded-xl border border-slate-200 px-3 py-2"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                updateCartLine(line.key, {
                                  quantity: line.stock > 0 ? Math.min(line.stock, line.quantity + 1) : line.quantity + 1,
                                })
                              }
                              className="rounded-xl border border-slate-200 p-2"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        </Field>
                      )}

                      <Field label={line.category === "PROPERTY" && line.transactionType === "RENT" ? "Montant location" : supportsCart ? "Prix unitaire" : "Prix de vente"}>
                        <input
                          type="number"
                          min={0}
                          value={line.unitPrice}
                          onChange={(e) => updateCartLine(line.key, { unitPrice: Number(e.target.value || 0) })}
                          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2"
                        />
                      </Field>
                    </div>

                    {line.category === "PROPERTY" && (
                      <>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                          <Field label="Type d'operation">
                            <select
                              value={line.transactionType}
                              onChange={(e) =>
                                updateCartLine(line.key, {
                                  transactionType: e.target.value,
                                  unitPrice: Number(
                                    resolvePropertyUnitPriceFromLine(line, e.target.value)
                                  ),
                                })
                              }
                              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2"
                            >
                              {resolveOperationOptions(line.listingType).map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </Field>

                          <Field label="Disponibilite apres transaction">
                            <select
                              value={line.rentalAvailability}
                              onChange={(e) => updateCartLine(line.key, { rentalAvailability: e.target.value })}
                              className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2"
                            >
                              <option value="OUI">Oui</option>
                              <option value="NON">Non</option>
                            </select>
                          </Field>
                        </div>

                        {line.transactionType === "RENT" && (
                          <div className="mt-4 grid gap-4 sm:grid-cols-3">
                            <Field label="Nombre de jours">
                              <input
                                type="number"
                                min={1}
                                value={line.stayDays}
                                onChange={(e) => updateCartLine(line.key, { stayDays: e.target.value })}
                                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2"
                              />
                            </Field>

                            <Field label="Debut de location">
                              <div className="relative mt-2">
                                <CalendarRange size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                  type="date"
                                  value={line.rentalStartDate}
                                  onChange={(e) => updateCartLine(line.key, { rentalStartDate: e.target.value })}
                                  className="w-full rounded-xl border border-slate-200 pl-9 px-3 py-2"
                                />
                              </div>
                            </Field>

                            <Field label="Fin de location">
                              <div className="relative mt-2">
                                <CalendarRange size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                  type="date"
                                  value={line.rentalEndDate}
                                  onChange={(e) => updateCartLine(line.key, { rentalEndDate: e.target.value })}
                                  className="w-full rounded-xl border border-slate-200 pl-9 px-3 py-2"
                                />
                              </div>
                            </Field>
                          </div>
                        )}
                      </>
                    )}

                    <div className="mt-4 flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 text-sm">
                      <span className="text-slate-500">Sous-total</span>
                      <span className="font-semibold text-slate-900">
                        {(line.unitPrice * line.quantity).toLocaleString()} FCFA
                      </span>
                    </div>
                  </div>
                ))}

                {!cart.length && (
                  <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-10 text-center text-sm text-slate-500">
                    Aucun produit ajoute pour le moment.
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStepId === "customer" && (
            <div className="space-y-6">
              <div ref={customerContainerRef} className="relative">
                <Field label="Choisir un client existant">
                  <div className="relative mt-2">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      placeholder="Rechercher par nom ou telephone..."
                      className="w-full rounded-2xl border border-slate-200 pl-9 px-3 py-3"
                    />
                  </div>
                </Field>

                {customerQuery.trim().length > 0 && filteredCustomers.length > 0 && (
                  <div className="absolute z-20 mt-2 max-h-72 w-full overflow-y-auto divide-y overflow-hidden rounded-2xl border bg-white shadow-xl">
                    {filteredCustomers.map((customer) => (
                      <button
                        key={customer.phone}
                        type="button"
                        onClick={() => selectExistingCustomer(customer)}
                        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
                      >
                        <div>
                          <p className="text-sm font-medium text-slate-800">{customer.name}</p>
                          <p className="mt-1 text-xs text-slate-500">{customer.phone}</p>
                        </div>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                          {customer.purchases} achat{customer.purchases > 1 ? "s" : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedExistingCustomer && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  Client selectionne: <strong>{selectedExistingCustomer.name}</strong> • {selectedExistingCustomer.phone}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Nom du client">
                  <div className="relative mt-2">
                    <UserRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={clientName}
                      onChange={(e) => {
                        setClientName(e.target.value)
                        setSelectedExistingCustomer(null)
                      }}
                      placeholder="Ex: Kouassi Laurent"
                      className="w-full rounded-2xl border border-slate-200 pl-9 px-3 py-3"
                    />
                  </div>
                </Field>

                <Field label="Telephone du client">
                  <input
                    value={clientPhone}
                    onChange={(e) => {
                      setClientPhone(e.target.value)
                      setSelectedExistingCustomer(null)
                    }}
                    placeholder="Ex: 0700000000"
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-3"
                  />
                </Field>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                Vous pouvez choisir un client deja connu ou saisir un nouveau client manuellement.
              </div>
            </div>
          )}

          {currentStepId === "payment" && (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Montant paye (FCFA)">
                  <input
                    type="number"
                    min={0}
                    value={paid}
                    onChange={(e) => setPaid(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-3"
                  />
                </Field>

                <Field label="Mode de paiement">
                  <div className="mt-2 grid gap-3">
                    <PaymentButton
                      active={paymentMethod === "CASH"}
                      onClick={() => setPaymentMethod("CASH")}
                      icon={<Banknote size={18} />}
                      title="Especes"
                      subtitle="Paiement cash"
                    />
                    <PaymentButton
                      active={paymentMethod === "DELIVERER"}
                      onClick={() => setPaymentMethod("DELIVERER")}
                      icon={<Truck size={18} />}
                      title={propertyStore ? "Courtier / agent" : "Livreur"}
                      subtitle={propertyStore ? "Paiement transmis par l'agent" : "Paiement a la livraison"}
                    />
                    <PaymentButton
                      active={paymentMethod === "MOBILE_MONEY"}
                      onClick={() => setPaymentMethod("MOBILE_MONEY")}
                      icon={<Smartphone size={18} />}
                      title="Mobile Money"
                      subtitle="Wave, Orange, MTN"
                      disabled={!hasMobileMoney}
                    />
                  </div>
                </Field>
              </div>

              {paymentMethod === "MOBILE_MONEY" && (
                <label className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={payOnline}
                    onChange={(e) => setPayOnline(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <span>Envoyer un lien de paiement au client.</span>
                </label>
              )}

              {!hasMobileMoney && (
                <p className="text-xs text-amber-600">
                  Mobile Money est verrouille sur votre plan actuel. Passez au plan PRO ou BUSINESS.
                </p>
              )}

              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2 text-slate-800">
                  <Receipt size={18} />
                  <p className="font-semibold">Recapitulatif final</p>
                </div>

                <div className="mt-4 space-y-3 text-sm">
                  <SummaryRow label="Client" value={clientName || "Nouveau client"} />
                  <SummaryRow label="Telephone" value={clientPhone || "-"} />
                  <SummaryRow label="Produits" value={`${cart.length} ligne${cart.length > 1 ? "s" : ""}`} />
                  <SummaryRow label="Total" value={`${totalAmount.toLocaleString()} FCFA`} strong />
                  <SummaryRow label="Paye" value={`${paidAmount.toLocaleString()} FCFA`} />
                  <SummaryRow
                    label="Reste"
                    value={`${remainingAmount.toLocaleString()} FCFA`}
                    accent={remainingAmount > 0 ? "danger" : "success"}
                  />
                </div>
              </div>
            </div>
          )}
        </section>

        <aside className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-800">
              <Boxes size={18} />
              <p className="font-semibold">Panier</p>
            </div>

            <div className="mt-4 space-y-3">
              {cart.map((line) => (
                <div key={line.key} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-800">{line.label}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {line.quantity} × {line.unitPrice.toLocaleString()} FCFA
                      </p>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {(line.quantity * line.unitPrice).toLocaleString()} FCFA
                    </span>
                  </div>
                </div>
              ))}

              {!cart.length && (
                <p className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
                  Votre panier est vide.
                </p>
              )}
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-slate-950 p-5 text-white shadow-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-400" />
              <p className="font-semibold">Validation</p>
            </div>
            <p className="mt-2 text-sm text-white/70">
              Pour les ventes multi-produits, le systeme cree une vente par produit avec le meme client et repartit automatiquement le montant paye.
            </p>

            <div className="mt-5 flex gap-3">
              <button
                onClick={goBack}
                className="rounded-2xl border border-white/15 px-4 py-3 text-sm text-white/85 transition hover:bg-white/10"
              >
                {activeStep === 0 ? "Annuler" : "Retour"}
              </button>
              {activeStep < STEPS.length - 1 ? (
                <button
                  onClick={goNext}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
                >
                  Continuer
                  <ChevronRight size={15} />
                </button>
              ) : (
                <button
                  onClick={handleConfirm}
                  disabled={submitting}
                  className="rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Validation..." : "Confirmer la vente"}
                </button>
              )}
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}

function buildCartLine(
  item: any,
  label: string,
  reference: string,
  stock: number,
  unitPrice: number,
  propertyStore: boolean
): Omit<CartLine, "key"> {
  const listingType = String(item?.listingType || "SALE").toUpperCase()
  const initialTransactionType = propertyStore
    ? resolveInitialTransactionType(listingType, "SALE")
    : "SALE"

  return {
    productId: String(item?._id || ""),
    label,
    reference,
    stock,
    quantity: 1,
    unitPrice: Number(unitPrice || 0),
    category: propertyStore ? "PROPERTY" : reference && item?.imei ? "PHONE" : "GENERAL",
    listingType,
    district: item?.district || "",
    location: item?.location || "",
    transactionType: initialTransactionType,
    stayDays: "",
    rentalStartDate: "",
    rentalEndDate: "",
    rentalAvailability: propertyStore ? "NON" : "OUI",
  }
}

function resolvePropertyUnitPriceFromLine(line: CartLine, transactionType: string) {
  if (transactionType === "RENT") {
    return line.unitPrice
  }

  return line.unitPrice
}

function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3 backdrop-blur-sm">
      <p className="text-xs uppercase tracking-[0.12em] text-white/75">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      {children}
    </label>
  )
}

function PaymentButton({
  active,
  onClick,
  icon,
  title,
  subtitle,
  disabled = false,
}: {
  active: boolean
  onClick: () => void
  icon: ReactNode
  title: string
  subtitle: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${
        active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:bg-slate-50"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg ${
          active ? "bg-white text-slate-900" : "bg-gray-100 text-gray-600"
        }`}
      >
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className={`text-xs ${active ? "text-white/70" : "text-gray-500"}`}>{subtitle}</p>
      </div>
    </button>
  )
}

function SummaryRow({
  label,
  value,
  strong,
  accent,
}: {
  label: string
  value: string
  strong?: boolean
  accent?: "danger" | "success"
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span
        className={`text-right ${
          accent === "danger"
            ? "font-semibold text-rose-600"
            : accent === "success"
              ? "font-semibold text-emerald-600"
              : strong
                ? "font-semibold text-slate-900"
                : "text-slate-700"
        }`}
      >
        {value}
      </span>
    </div>
  )
}

function resolveSuggestedPropertyAmount(item: any, transactionType: string) {
  if (transactionType === "RENT") {
    return item?.dailyRentalPrice || item?.monthlyRentalPrice || item?.sellPrice || item?.price || 0
  }

  return item?.sellPrice || item?.price || item?.monthlyRentalPrice || item?.dailyRentalPrice || 0
}

function resolveInitialTransactionType(listingType: string, currentType: string) {
  const normalizedListingType = String(listingType || "SALE").toUpperCase()
  const normalizedCurrentType = String(currentType || "SALE").toUpperCase()

  if (normalizedListingType === "RENT") return "RENT"
  if (normalizedListingType === "SALE") return "SALE"
  if (normalizedCurrentType === "RENT" || normalizedCurrentType === "SALE") return normalizedCurrentType
  return "SALE"
}

function resolveOperationOptions(listingType?: string) {
  const normalizedListingType = String(listingType || "BOTH").toUpperCase()

  if (normalizedListingType === "SALE") {
    return [{ value: "SALE", label: "vente" }]
  }

  if (normalizedListingType === "RENT") {
    return [{ value: "RENT", label: "location" }]
  }

  return [
    { value: "SALE", label: "vente" },
    { value: "RENT", label: "location" },
  ]
}
